import { useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router';
import { Check } from 'lucide-react';
import { Menu, MenuItem } from '../ui/Menu';
import { formatPrice } from '../ui/price';
import type { SortBinding } from '../ui/useDragSort';
import { HighlightQuery } from '../search/Highlight';
import { useImageUrl } from '../db/useImageUrl';
import type { Item } from '../db/schema';
import type { Placement } from './masonry';
import { useNearViewport } from './nearViewport';
import { SectionCover } from './SectionCover';
import type { Modifiers } from './useSelection';

/**
 * Absolutely positioned inside the board's relative container and moved with
 * `transform: translate()` rather than `top`/`left`, so the browser composites
 * the move on the GPU when the panel opens and the columns recount.
 *
 * The frame is sized from the database before any pixel arrives — a `--sand`
 * rectangle that the photo fades into. Nothing shifts as images decode.
 */
export function Tile({
  item,
  placement,
  base,
  selected,
  picked,
  highlight,
  animate,
  lifted,
  merging,
  mergeCovers,
  sort,
  onPick,
  onMove,
  onDelete,
}: {
  item: Item;
  placement: Placement;
  /** The container's address; the panel opens as a segment under it. */
  base: string;
  selected: boolean;
  /** Part of a multi-selection. Distinct from `selected`, which is the panel. */
  picked: boolean;
  /** The search this tile matched, lowercased, marked in the caption. */
  highlight: string | undefined;
  /** False until the first layout pass has painted — see `boardStore`. */
  animate: boolean;
  /** Being dragged: off the page, following the cursor. */
  lifted: boolean;
  /** Something is being held over it, and would group with it on release. */
  merging: boolean;
  /** The two covers that grouping would put in the new section. */
  mergeCovers: readonly string[];
  sort: SortBinding;
  /** True if the click was a selection gesture and shouldn't open the panel. */
  onPick: (modifiers: Modifiers) => boolean;
  onMove: () => void;
  onDelete: () => void;
}) {
  const ref = useRef<HTMLElement>(null);
  const near = useNearViewport(ref);
  // Undefined while the tile is far away: that releases the object URL and
  // lets the decoded bitmap go with it.
  const url = useImageUrl(near ? item.coverImageId : undefined);
  const [readyUrl, setReadyUrl] = useState<string | undefined>(undefined);

  const classes = [
    'tile',
    animate ? 'tile--animate' : '',
    selected ? 'tile--selected' : '',
    picked ? 'tile--picked' : '',
    lifted ? 'lifted' : '',
    merging ? 'tile--merging' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const price = formatPrice(item.price, item.currency);

  return (
    <article
      ref={ref}
      className={classes}
      // Custom properties rather than `transform` directly: while this tile is
      // lifted the drag owns its transform, and the two would overwrite each
      // other if they wrote to the same declaration.
      style={
        {
          '--tile-x': `${placement.x}px`,
          '--tile-y': `${placement.y}px`,
          width: `${placement.w}px`,
        } as CSSProperties
      }
      {...sort}
    >
      <Link
        to={`${base}/${item.id}`}
        className="tile__link"
        aria-current={selected ? 'true' : undefined}
        // An anchor is draggable by default, and the browser's own link drag
        // would fight this one for the gesture.
        draggable={false}
        onClick={(event) => {
          // Shift and Cmd/Ctrl pick tiles instead of opening them. A plain
          // click drops the selection and follows the link, as it always did.
          if (onPick(event)) event.preventDefault();
        }}
      >
        <div className="tile__frame" style={{ height: `${placement.h}px` }}>
          {url !== undefined && (
            <img
              className={`tile__image${readyUrl === url ? ' tile__image--ready' : ''}`}
              src={url}
              alt=""
              draggable={false}
              onLoad={() => setReadyUrl(url)}
            />
          )}

          {/* What releasing would make, drawn on top of what's there now.
              The preview is the same component the real section uses, so
              it isn't a promise of something that then looks different. */}
          {merging && (
            <div className="tile__merge">
              <SectionCover coverImageId={undefined} covers={mergeCovers} />
            </div>
          )}

          {picked && (
            <span className="tile__check" aria-hidden>
              <Check size={14} />
            </span>
          )}
        </div>

        <div className={`tile__caption${price === undefined ? '' : ' tile__caption--priced'}`}>
          <p className="tile__title">
            {highlight === undefined ? (
              item.title
            ) : (
              <HighlightQuery text={item.title} query={highlight} />
            )}
          </p>
          {price !== undefined && <p className="tile__price">{price}</p>}
        </div>
      </Link>

      {/* The affordance the design system puts at the top-right of the photo.
          Editing lives in the panel a click away, so this is for the two things
          the panel shouldn't be the only route to. */}
      <div className="tile__actions">
        <Menu label={`Actions for ${item.title}`}>
          {(close) => (
            <>
              <MenuItem
                onSelect={() => {
                  close();
                  onMove();
                }}
              >
                Move To Room
              </MenuItem>
              <MenuItem
                onSelect={() => {
                  close();
                  onDelete();
                }}
              >
                Delete Item
              </MenuItem>
            </>
          )}
        </Menu>
      </div>
    </article>
  );
}
